import type { Aabb, PlayerInput, PlayerState, Vec3 } from "./types";
import {
  AIR_ACCEL, AIR_WISH_SPEED,
  COYOTE_TIME, JUMP_BUFFER_TIME,
  DODGE_COOLDOWN_S, DODGE_DURATION_S, DODGE_SPEED, DODGE_UPWARD_BOOST,
  GRAVITY, GROUND_ACCEL, GROUND_FRICTION, STOP_SPEED,
  JUMP_VELOCITY,
  PLAYER_HEIGHT, PLAYER_RADIUS, PLAYER_SPEED, PLAYER_SPRINT_MULTIPLIER,
} from "./constants";

const _min: Vec3 = { x: 0, y: 0, z: 0 };
const _max: Vec3 = { x: 0, y: 0, z: 0 };

function setAabb(p: Vec3) {
  _min.x = p.x - PLAYER_RADIUS; _min.y = p.y; _min.z = p.z - PLAYER_RADIUS;
  _max.x = p.x + PLAYER_RADIUS; _max.y = p.y + PLAYER_HEIGHT; _max.z = p.z + PLAYER_RADIUS;
}

function overlaps(c: Aabb): boolean {
  return (
    _min.x < c.max.x && _max.x > c.min.x &&
    _min.y < c.max.y && _max.y > c.min.y &&
    _min.z < c.max.z && _max.z > c.min.z
  );
}

function applyFriction(state: PlayerState, dt: number) {
  const speed = Math.hypot(state.velocity.x, state.velocity.z);
  if (speed < 0.01) { state.velocity.x = 0; state.velocity.z = 0; return; }
  const control = speed < STOP_SPEED ? STOP_SPEED : speed;
  const drop = control * GROUND_FRICTION * dt;
  const newSpeed = Math.max(0, speed - drop);
  const scale = newSpeed / speed;
  state.velocity.x *= scale;
  state.velocity.z *= scale;
}

function applyAccel(state: PlayerState, wishDirX: number, wishDirZ: number, wishSpeed: number, accel: number, dt: number) {
  const cur = state.velocity.x * wishDirX + state.velocity.z * wishDirZ;
  const add = wishSpeed - cur;
  if (add <= 0) return;
  const a = Math.min(accel * wishSpeed * dt, add);
  state.velocity.x += a * wishDirX;
  state.velocity.z += a * wishDirZ;
}

export function stepPlayer(state: PlayerState, input: PlayerInput, colliders: Aabb[]): void {
  const dt = Math.min(input.dt, 0.1);

  state.coyoteTimer = Math.max(0, state.coyoteTimer - dt);
  state.dodgingTimer = Math.max(0, state.dodgingTimer - dt);
  state.dodgeCooldownTimer = Math.max(0, state.dodgeCooldownTimer - dt);
  if (input.jump) state.jumpBufferTimer = JUMP_BUFFER_TIME;
  else state.jumpBufferTimer = Math.max(0, state.jumpBufferTimer - dt);

  // wish direction
  let fwd = input.forward, rt = input.right;
  const mag = Math.hypot(fwd, rt);
  let wdx = 0, wdz = 0;
  if (mag > 0) {
    fwd /= mag; rt /= mag;
    const sinY = Math.sin(input.yaw), cosY = Math.cos(input.yaw);
    wdx = -sinY * fwd + cosY * rt;
    wdz = -cosY * fwd - sinY * rt;
  }

  // Dodge: edge-triggered burst of velocity in the wish direction
  // (or backward relative to look if no movement input).
  if (input.dodge && state.dodgeCooldownTimer <= 0) {
    let dx = wdx, dz = wdz;
    if (mag === 0) {
      // backward: opposite of forward look
      dx = Math.sin(input.yaw);
      dz = Math.cos(input.yaw);
    }
    state.velocity.x = dx * DODGE_SPEED;
    state.velocity.z = dz * DODGE_SPEED;
    if (state.onGround) state.velocity.y = Math.max(state.velocity.y, DODGE_UPWARD_BOOST);
    state.dodgingTimer = DODGE_DURATION_S;
    state.dodgeCooldownTimer = DODGE_COOLDOWN_S;
  }

  const speedMul = state.speedMultiplier > 0 ? state.speedMultiplier : 1;
  const maxSpeed = PLAYER_SPEED * speedMul * (input.sprint ? PLAYER_SPRINT_MULTIPLIER : 1);

  // While diving, lock out friction and accel — the burst rides out cleanly.
  if (state.dodgingTimer <= 0) {
    if (state.onGround) {
      applyFriction(state, dt);
      if (mag > 0) applyAccel(state, wdx, wdz, maxSpeed, GROUND_ACCEL, dt);
    } else if (mag > 0) {
      const airWish = Math.min(AIR_WISH_SPEED, maxSpeed);
      applyAccel(state, wdx, wdz, airWish, AIR_ACCEL, dt);
    }
  }

  if (state.jumpBufferTimer > 0 && (state.onGround || state.coyoteTimer > 0)) {
    state.velocity.y = JUMP_VELOCITY;
    state.onGround = false;
    state.coyoteTimer = 0;
    state.jumpBufferTimer = 0;
  }

  state.velocity.y -= GRAVITY * dt;

  // X
  state.position.x += state.velocity.x * dt;
  setAabb(state.position);
  for (const c of colliders) {
    if (!overlaps(c)) continue;
    if (state.velocity.x > 0) state.position.x = c.min.x - PLAYER_RADIUS - 1e-4;
    else if (state.velocity.x < 0) state.position.x = c.max.x + PLAYER_RADIUS + 1e-4;
    state.velocity.x = 0;
    setAabb(state.position);
  }

  // Y
  state.position.y += state.velocity.y * dt;
  setAabb(state.position);
  let landed = false;
  for (const c of colliders) {
    if (!overlaps(c)) continue;
    if (state.velocity.y > 0) {
      state.position.y = c.min.y - PLAYER_HEIGHT - 1e-4;
    } else if (state.velocity.y < 0) {
      state.position.y = c.max.y + 1e-4;
      landed = true;
    }
    state.velocity.y = 0;
    setAabb(state.position);
  }
  state.onGround = landed;
  if (state.onGround) state.coyoteTimer = COYOTE_TIME;

  if (landed && state.jumpBufferTimer > 0) {
    state.velocity.y = JUMP_VELOCITY;
    state.onGround = false;
    state.coyoteTimer = 0;
    state.jumpBufferTimer = 0;
  }

  // Z
  state.position.z += state.velocity.z * dt;
  setAabb(state.position);
  for (const c of colliders) {
    if (!overlaps(c)) continue;
    if (state.velocity.z > 0) state.position.z = c.min.z - PLAYER_RADIUS - 1e-4;
    else if (state.velocity.z < 0) state.position.z = c.max.z + PLAYER_RADIUS + 1e-4;
    state.velocity.z = 0;
    setAabb(state.position);
  }
}
